/**
 * Single-modal Create / Edit Group: details + facilitators + schedule.
 */
import {
  ActionIcon,
  Alert,
  Button,
  Divider,
  Group,
  Modal,
  MultiSelect,
  NumberInput,
  Select,
  Stack,
  Text,
  TextInput,
  Textarea,
} from '@mantine/core';
import { IconX } from '@tabler/icons-react';
import { useEffect, useMemo, useRef, useState } from 'react';

import { formatDateOnlyLocal, parseDateOnlyLocal } from '@/lib/dateHelpers';
import { applyZonedWallClock } from '@/lib/unifyDateFormat';
import { parseTimeLabel12h } from '@/features/calendar/providerAvailabilityValidation';
import { isIntervalWithinLocationHours, resolveEffectiveWeeklyHours } from '@unify/fhir-model/resources/location';
import { useLocationResources } from '@/hooks/useLocationResources';
import { useRoomAvailabilityForDate } from '@/hooks/useRoomAvailabilityForDate';
import {
  ENROLLMENT_MODE_OPTIONS,
  facilitatorReferencesFromForm,
  hhmmToMinutes,
  listGroupSetupSessionDates,
  seedGroupFormFromTemplate,
  type GroupEnrollmentMode,
  type GroupSetupFormValues,
  type GroupTemplateListItem,
  validateGroupSetupForm,
} from '@/lib/groupTherapy';
import {
  GroupAppointmentScheduleFields,
  type GroupAppointmentScheduleFieldsValue,
} from '@/pages/calendar/components/GroupAppointmentScheduleFields';

import { GroupActivationChecklist } from './GroupActivationChecklist';
import {
  applyScheduleFieldsToGroup,
  scheduleFieldsFromGroup,
} from './groupScheduleFormHelpers';

function todayDateOnly(): string {
  return formatDateOnlyLocal(new Date());
}

const BORDER_COLOR = 'var(--mantine-color-gray-5)';

const DEFAULTS: GroupSetupFormValues = {
  active: false,
  name: '',
  enrollmentMode: 'closed',
  additionalFacilitatorReferences: [],
  startDate: '',
  daysOfWeek: [],
  startTime: '18:00',
  endTime: '19:00',
  timezone: 'America/Los_Angeles',
  recurrence: 'weekly',
  sessionDates: [],
  sessionCount: 8,
  minEnrollment: 4,
  maxEnrollment: 10,
};

export interface GroupSetupFormModalProps {
  opened: boolean;
  title: string;
  initialValues?: GroupSetupFormValues;
  templates: GroupTemplateListItem[];
  providers: { value: string; label: string; preferredLabel?: string; practiceId: string }[];
  /** Editing an already-active cohort: save keeps it active and reconciles the series. */
  wasActive?: boolean;
  saving?: boolean;
  onClose: () => void;
  onSubmit: (values: GroupSetupFormValues, activate: boolean) => void | Promise<void>;
}

export function GroupSetupFormModal({
  opened,
  title,
  initialValues,
  templates,
  providers,
  wasActive = false,
  saving = false,
  onClose,
  onSubmit,
}: GroupSetupFormModalProps): JSX.Element {
  const [values, setValues] = useState<GroupSetupFormValues>(DEFAULTS);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [schedule, setSchedule] = useState<GroupAppointmentScheduleFieldsValue>(() =>
    scheduleFieldsFromGroup(DEFAULTS, ''),
  );

  const { sites, roomsBySiteId, equipmentBySiteId } = useLocationResources({
    organizationId: values.organizationId ?? '',
    organizationDisplay: values.organizationName,
  });
  const firstSiteId = sites[0]?.id ?? '';
  const siteOptions = useMemo(
    () =>
      sites.map((s) => ({
        value: s.id,
        label: [s.name, s.addressLine1].filter(Boolean).join(' — '),
      })),
    [sites],
  );

  const templateOptions = useMemo(
    () =>
      templates
        .filter((t) => t.active || t.reference === values.templateReference)
        .map((t) => ({ value: t.reference, label: t.name })),
    [templates, values.templateReference],
  );

  const providerOptions = useMemo(
    () =>
      providers
        .filter((provider) =>
          !values.organizationId || provider.practiceId === values.organizationId,
        )
        .map(({ value, label, preferredLabel }) => ({ value, label, preferredLabel })),
    [providers, values.organizationId],
  );

  const firstSiteIdRef = useRef(firstSiteId);
  firstSiteIdRef.current = firstSiteId;

  // Seed once per open. Sites and templates resolve asynchronously, so re-running on
  // their arrival would wipe in-progress edits seconds after the modal opens.
  const seededRef = useRef(false);
  const initialValuesRef = useRef(initialValues);
  initialValuesRef.current = initialValues;

  useEffect(() => {
    if (!opened) {
      seededRef.current = false;
      return;
    }
    if (seededRef.current) return;
    seededRef.current = true;
    const next = { ...DEFAULTS, ...initialValuesRef.current };
    setValues(next);
    setErrors({});
    setSchedule(scheduleFieldsFromGroup(next, firstSiteIdRef.current));
  }, [opened]);

  // Sites resolve after open / template apply. Backfill location into schedule state so
  // Room/Equipment (which key off schedulingSiteId) appear for in-person/hybrid.
  useEffect(() => {
    if (!opened || !firstSiteId) return;
    setSchedule((prev) => {
      const needsLocation =
        (prev.modality === 'in-person' || prev.modality === 'hybrid')
        && (!prev.locationId || prev.locationId === 'telehealth');
      const needsOffice = !prev.officeForTelehealth;
      if (!needsLocation && !needsOffice) return prev;
      return {
        ...prev,
        ...(needsLocation ? { locationId: firstSiteId } : {}),
        ...(needsOffice ? { officeForTelehealth: firstSiteId } : {}),
      };
    });
  }, [opened, firstSiteId]);

  const patch = (partial: Partial<GroupSetupFormValues>) => {
    setValues((prev) => ({ ...prev, ...partial }));
  };

  const applyTemplate = (templateRef: string | null) => {
    if (!templateRef) {
      patch({ templateReference: undefined, templateDisplay: undefined });
      return;
    }
    const tmpl = templates.find((t) => t.reference === templateRef);
    if (!tmpl) {
      patch({ templateReference: templateRef });
      return;
    }
    const seeded = seedGroupFormFromTemplate(tmpl);
    setValues((prev) => {
      const next = { ...prev, ...seeded };
      setSchedule(scheduleFieldsFromGroup(next, firstSiteIdRef.current));
      return next;
    });
  };

  const schedulingSiteId =
    schedule.modality === 'telehealth'
      ? schedule.telehealthFromOffice
        ? schedule.officeForTelehealth
        : ''
      : schedule.locationId !== 'telehealth'
        ? schedule.locationId
        : '';

  const schedulingRooms = useMemo(() => {
    const rooms = roomsBySiteId[schedulingSiteId] ?? [];
    return rooms.filter((r) => r.status === 'active');
  }, [roomsBySiteId, schedulingSiteId]);

  const portableDevices = useMemo(
    () =>
      (equipmentBySiteId[schedulingSiteId] ?? []).filter(
        (e) => e.portability === 'portable' && e.status === 'active',
      ),
    [equipmentBySiteId, schedulingSiteId],
  );
  const fixedInRoom = useMemo(
    () =>
      (equipmentBySiteId[schedulingSiteId] ?? []).filter(
        (e) =>
          e.portability === 'fixed'
          && e.status === 'active'
          && e.homeRoomId === schedule.roomId,
      ),
    [equipmentBySiteId, schedulingSiteId, schedule.roomId],
  );

  const mergedPreview = useMemo(
    () => applyScheduleFieldsToGroup(values, schedule, sites),
    [values, schedule, sites],
  );

  const sessionWindow = useMemo(() => {
    const start = hhmmToMinutes(mergedPreview.startTime);
    const end = hhmmToMinutes(mergedPreview.endTime);
    if (start == null || end == null || end <= start) {
      return { start: null as number | null, end: null as number | null };
    }
    return { start, end };
  }, [mergedPreview.startTime, mergedPreview.endTime]);

  const sessionDatesYmd = useMemo(
    () => listGroupSetupSessionDates(mergedPreview, { todayYmd: todayDateOnly() }),
    [mergedPreview],
  );

  const {
    busyByRoom,
    busyByDevice,
    conflictedRoomIds: busyConflictedRoomIds,
    conflictedDeviceIds,
    loading: availabilityLoading,
  } = useRoomAvailabilityForDate({
    date: schedule.date,
    roomIds: schedulingRooms.map((r) => r.id),
    deviceIds: portableDevices.map((d) => d.id),
    sessionDatesYmd,
    windowStartMinutes: sessionWindow.start,
    windowEndMinutes: sessionWindow.end,
  });

  const scheduleTimeZone = values.timezone || 'America/Los_Angeles';
  const conflictedRoomIds = useMemo(() => {
    const ids = new Set(busyConflictedRoomIds);
    const s = parseTimeLabel12h(schedule.startTime);
    const e = parseTimeLabel12h(schedule.endTime);
    const siteHours = sites.find((site) => site.id === schedulingSiteId)?.hours;
    if (!s || !e) return ids;
    const dates = sessionDatesYmd.length
      ? sessionDatesYmd
      : schedule.date
        ? [formatDateOnlyLocal(schedule.date)]
        : [];
    for (const ymd of dates) {
      const day = parseDateOnlyLocal(ymd);
      if (!day) continue;
      const start = applyZonedWallClock(day, s.hour, s.minute, scheduleTimeZone);
      const end = applyZonedWallClock(day, e.hour, e.minute, scheduleTimeZone);
      if (end <= start) continue;
      for (const room of schedulingRooms) {
        const hours = resolveEffectiveWeeklyHours(siteHours, room.hours);
        if (!isIntervalWithinLocationHours(start.toISOString(), end.toISOString(), hours)) {
          ids.add(room.id);
        }
      }
    }
    return ids;
  }, [
    busyConflictedRoomIds,
    schedule.startTime,
    schedule.endTime,
    schedule.date,
    sessionDatesYmd,
    sites,
    schedulingSiteId,
    schedulingRooms,
    scheduleTimeZone,
  ]);

  const buildPayload = (activate: boolean): GroupSetupFormValues => {
    const applied = applyScheduleFieldsToGroup(values, schedule, sites);
    return {
      ...applied,
      facilitatorDisplays: facilitatorReferencesFromForm(applied).map(
        (id) => providerOptions.find((p) => p.value === id)?.preferredLabel ?? '',
      ),
      active: activate,
    };
  };

  const submit = (activate: boolean) => {
    const payload = buildPayload(activate);
    // Draft save: only require template/name/capacity; schedule validated on activate.
    const validation = activate
      ? validateGroupSetupForm(payload, { forActivation: true })
      : (() => {
          const next: { field: string; message: string }[] = [];
          if (!payload.templateReference?.trim()) {
            next.push({ field: 'templateReference', message: 'Select a Group Template.' });
          }
          if (!payload.name.trim()) {
            next.push({ field: 'name', message: 'Group name is required.' });
          }
          if (!payload.organizationId?.trim()) {
            next.push({ field: 'organizationId', message: 'Practice is required.' });
          }
          if (!payload.minEnrollment || payload.minEnrollment <= 0) {
            next.push({
              field: 'minEnrollment',
              message: 'Minimum enrollment must be greater than 0.',
            });
          }
          if (!payload.maxEnrollment || payload.maxEnrollment < payload.minEnrollment) {
            next.push({
              field: 'maxEnrollment',
              message: 'Maximum must be ≥ minimum.',
            });
          }
          return next;
        })();
    if (validation.length) {
      const map: Record<string, string> = {};
      for (const err of validation) {
        if (err.field !== 'general') map[err.field] = err.message;
      }
      setErrors(map);
      return;
    }
    void onSubmit(payload, activate);
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      withCloseButton={false}
      title={
        <Group justify="space-between" align="center" style={{ width: '100%' }}>
          <Text fw={700} size="lg">
            {title}
          </Text>
          <ActionIcon
            size="lg"
            variant="subtle"
            color="gray"
            onClick={onClose}
            aria-label="Close group setup"
          >
            <IconX size={20} />
          </ActionIcon>
        </Group>
      }
      size="720px"
      centered
      closeOnClickOutside
      styles={{
        header: { borderBottom: `1px solid ${BORDER_COLOR}`, paddingBottom: 16 },
        title: { width: '100%', flex: 1 },
        body: { paddingTop: 20 },
        content: { border: `1px solid ${BORDER_COLOR}` },
      }}
    >
      <Stack gap="md">
        <Select
          label="Group Template"
          required
          data={templateOptions}
          value={values.templateReference ?? null}
          error={errors.templateReference}
          onChange={applyTemplate}
          searchable
          nothingFoundMessage="No templates — create one first"
        />
        <TextInput
          label="Group name"
          required
          value={values.name}
          error={errors.name}
          onChange={(e) => patch({ name: e.currentTarget.value })}
        />
        <Text size="sm" c="dimmed">
          Practice: {values.organizationName ?? '—'}
          {errors.organizationId ? (
            <Text span c="red" size="sm">
              {' '}
              ({errors.organizationId})
            </Text>
          ) : null}
        </Text>
        <Select
          label="Enrollment mode"
          description="Defaults from the template; you can change it for this group."
          required
          data={ENROLLMENT_MODE_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
          value={values.enrollmentMode}
          onChange={(v) => patch({ enrollmentMode: (v as GroupEnrollmentMode) || 'closed' })}
        />
        <Textarea
          label="Description"
          description="Defaults from the template; you can change it for this group."
          minRows={2}
          value={values.description ?? ''}
          onChange={(e) => patch({ description: e.currentTarget.value })}
        />
        <Group grow>
          <NumberInput
            label="Min capacity"
            min={1}
            value={values.minEnrollment}
            error={errors.minEnrollment}
            onChange={(v) => patch({ minEnrollment: Number(v) || 1 })}
          />
          <NumberInput
            label="Max capacity"
            min={1}
            value={values.maxEnrollment}
            error={errors.maxEnrollment}
            onChange={(v) => patch({ maxEnrollment: Number(v) || 1 })}
          />
        </Group>

        <Divider label="Facilitators" labelPosition="left" />

        <Select
          label="Primary facilitator"
          required
          data={providerOptions}
          value={values.primaryFacilitatorReference ?? null}
          error={errors.primaryFacilitatorReference}
          onChange={(reference) =>
            setValues((prev) => ({
              ...prev,
              primaryFacilitatorReference: reference ?? undefined,
            }))
          }
          searchable
        />
        <MultiSelect
          label="Additional facilitators"
          data={providerOptions.filter(
            (provider) => provider.value !== values.primaryFacilitatorReference,
          )}
          value={values.additionalFacilitatorReferences ?? []}
          onChange={(references) =>
            setValues((prev) => ({
              ...prev,
              additionalFacilitatorReferences: references,
            }))
          }
          searchable
        />

        <Divider label="Schedule" labelPosition="left" />

        <GroupAppointmentScheduleFields
          value={
            schedule.locationId || !firstSiteId
              ? schedule
              : {
                  ...schedule,
                  locationId:
                    schedule.modality === 'telehealth'
                      ? 'telehealth'
                      : firstSiteId,
                  officeForTelehealth: schedule.officeForTelehealth || firstSiteId,
                }
          }
          onChange={(patchSchedule) => setSchedule((prev) => ({ ...prev, ...patchSchedule }))}
          sites={sites}
          siteOptions={siteOptions}
          firstSiteId={firstSiteId}
          schedulingRooms={schedulingRooms}
          portableDevices={portableDevices}
          fixedInRoom={fixedInRoom}
          busyByRoom={busyByRoom}
          busyByDevice={busyByDevice}
          conflictedRoomIds={conflictedRoomIds}
          conflictedDeviceIds={conflictedDeviceIds}
          availabilityLoading={availabilityLoading}
          groupSeriesLayout
          showEnrollmentLockDate={values.enrollmentMode === 'closed'}
          enrollmentLockDate={
            values.enrollmentLockDate
              ? parseDateOnlyLocal(values.enrollmentLockDate)
              : null
          }
          onEnrollmentLockDateChange={(date) =>
            setValues((prev) => ({
              ...prev,
              enrollmentLockDate: date ? formatDateOnlyLocal(date) : undefined,
            }))
          }
          enrollmentLockDateError={errors.enrollmentLockDate}
          displayTimeZone={scheduleTimeZone}
        />

        {!wasActive && <GroupActivationChecklist values={mergedPreview} />}

        {Object.keys(errors).length > 0 && (
          <Alert color="red" title="Fix form errors">
            <Stack gap={4}>
              {Object.entries(errors).map(([field, message]) => (
                <span key={field}>{message}</span>
              ))}
            </Stack>
          </Alert>
        )}

        <Divider />
        <Group justify="flex-end">
          <Button variant="default" disabled={saving} onClick={onClose}>
            Cancel
          </Button>
          {!wasActive && (
            <Button variant="outline" loading={saving} onClick={() => submit(false)}>
              Save as draft
            </Button>
          )}
          <Button color="gold" loading={saving} onClick={() => submit(true)}>
            {wasActive ? 'Save & reconcile' : 'Save & activate'}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
